import { request, getApiUrl } from '@/utils/ajax';
import { ExternalAgreement, ProjectTag, ExternalAgreementStatus, ExternalAgreementFilterData, OptionGroupOverrideSelection } from '@/types';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';

export interface CreateExternalAgreementOptions {
    documentTypeId: number;
    name: string;
    residentialUnitId: number;
    participantsNote: string;
    profileId: number;
    files: ExternalAgreementFileCreateOptions[];
    lines: ExternalAgreementLineCreateOptions[];
    participants: ExternalAgreementParticipantCreateOptions[];
}

export interface ExternalAgreementFileCreateOptions {
    sortOrder: number;
    name: string;
    isSensitive: boolean;
    hideFromBuyer: boolean;
    file: FileData;
}

export interface ExternalAgreementLineCreateOptions {
    description: string;
    price: number | null;
    projectFeeSetId: number;
    itemCategoryId: number | null | undefined;
    tags: ProjectTag[];
}

export interface ExternalAgreementParticipantCreateOptions {
    profileId: number;
    userId: number;
}

export function createExternalAgreement(data: CreateExternalAgreementOptions) {
    return request(`api/external-agreement`, {
        method: 'POST',
        json: true,
        body: data,
    });
}

export interface UpdateExternalAgreementOptions {
    participantsNote?: string;

    lines?: ExternalAgreementLineUpdateOptions[];
    participants?: ExternalAgreementParticipantUpdateOptions[];
    files?: ExternalAgreementFileUpdateOptions[];
}

export interface ExternalAgreementLineUpdateOptions {
    id?: number;
    description: string;
    price: number | null;
    projectFeeSetId: number;
    tags: ProjectTag[];
}

export interface ExternalAgreementParticipantUpdateOptions {
    profileId: number;
    userId: number;
}

export interface ExternalAgreementFileUpdateOptions {
    id?: number;
    sortOrder: number;
    name: string;
    isSensitive: boolean;
    hideFromBuyer: boolean;
    file: FileData;
}

export function updateExternalAgreement(id: number, data: UpdateExternalAgreementOptions) {
    return request(`api/external-agreement/${id}`, {
        method: 'PUT',
        json: true,
        body: data,
    }).then<ExternalAgreement>((x) => x.json());
}
// OptionalId < ChangeRequestParticipant > [];

export interface SearchExternalAgreementsOptions {
    projectId?: number;
    residentialUnitIds?: number[];
    residentialUnitGroupIds?: number[];
    statuses?: ExternalAgreementStatus[];
    searchText?: string;
    approvableOnly?: boolean;
}
export function searchExternalAgreements(options: SearchExternalAgreementsOptions): Promise<ExternalAgreement[]> {
    return request(`api/external-agreement`, { query: options }).then((x) => x.json());
}

export function getExternalAgreementFilterData(projectId: number): Promise<ExternalAgreementFilterData> {
    return request(`api/external-agreement/filter-data`, { query: { projectId } }).then((r) => r.json());
}

export interface ApproveExternalAgreementOptions {
    participants: ExternalAgreementParticipantCreateOptions[];
    changeRequestOptions: ApproveExternalAgreementChangeRequestOptions;
    offerSendNote: string;
    overrideBuyerOptionGroupIds: OptionGroupOverrideSelection[];
    preSigned: boolean;
    showSubtotalsToBuyers: boolean;
}

export interface ApproveExternalAgreementChangeRequestOptions {
    subject: string;
    description: string;
    internalDescription: string;
}

export function approveExternalAgreement(externalAgreementId, options: ApproveExternalAgreementOptions) {
    return request(`api/external-agreement/${externalAgreementId}/approve`, {
        method: 'POST',
        json: true,
        body: options,
    });
}

export function rejectExternalAgreement(externalAgreementId: number, reason: string, sendRejectEmail: boolean) {
    return request(`api/external-agreement/${externalAgreementId}/reject`, {
        method: 'POST',
        json: true,
        body: {
            reason,
            sendRejectEmail,
        },
    });
}

export function getDownloadExternalAgreementFileUrl(fileId: number) {
    return getApiUrl() + `/api/external-agreement-file/${fileId}/attachment/download`;
}

export function getOpenExternalAgreementFileUrl(fileId: number) {
    return getApiUrl() + `/api/external-agreement-file/${fileId}/attachment/open`;
}

export function getChangeExternalAgreementFileUrl(fileId: number) {
    return getApiUrl() + `/api/external-agreement-file/${fileId}/attachment/thumbnail`;
}

export function getExternalAgreement(id: number): Promise<ExternalAgreement> {
    return request(`api/external-agreement/${id}`).then((x) => x.json());
}

export function setArchivedExternalAgreement(externalAgreementId: number, isArchived: boolean) {
    return request(`api/external-agreement/${externalAgreementId}/archive`, {
        method: 'PATCH',
        json: true,
        body: { isArchived },
    });
}